import get from 'lodash/get';
import { action, computed, makeObservable, observable } from 'mobx';

import { GAME_PAGINATION_LIMIT } from '../../config/constants';
import { SupportedLanguages } from '../../i18n/constants';
import { getRequest } from '../../services/api';
import { resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types.js';
import { getPagination } from '../../utils/getPagination';
import { HOME_CATEGORY_ID, SEARCH_CATEGORY_ID } from '../categories/CategoriesStore';
import type { SubCategoryGamesResponse } from '../categories/CategoriesStore.js';
import type { GamesResponse } from '../game/types';
import type { RootStore } from '../RootStore.js';

export class GamesSearchStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
    void this.syncLocalSearchData();

    window.addEventListener('beforeunload', this.onPageClose);
  }

  @observable public searchHistory: string[] = [];
  @action
  private setSearchHistory(value: string[]) {
    this.searchHistory = value;
  }

  @observable
  public gameSubCategorySearchId: number | null = null;

  @observable
  public searchTerm: string = '';

  @observable
  public isSearchInputOpen: boolean = false;

  @observable
  public isSearchInputOpenV2: boolean = false;

  @observable
  public isRecentlyPlayedShowed = false;

  @action
  private setRecentlyPlayedShowed(value: boolean) {
    this.isRecentlyPlayedShowed = value;
  }

  @computed
  public get isSearchActivated(): boolean {
    return this.isSearchInputOpen || !!this.searchTerm.length;
  }

  public get searchGamesLimit() {
    const isOldSearchMode = resolveFlag('enable_old_search_mode', false);

    if (isOldSearchMode) {
      return 0;
    }

    const width = window.innerWidth;
    if (width > 768) {
      return 12;
    }
    return 6;
  }

  @action
  public setSearchTerm(newTerm: string) {
    const prevSearchTerm = this.searchTerm;
    this.searchTerm = newTerm;

    if (prevSearchTerm !== newTerm) {
      void this.handleSearchInputChange(newTerm);
    }
  }

  @action
  public setIsSearched(isSearchInputOpen: boolean) {
    this.isSearchInputOpen = isSearchInputOpen;
  }

  @action
  private _openSearch() {
    this.isSearchInputOpenV2 = true;
  }

  @action
  private _closeSearch() {
    this.isSearchInputOpenV2 = false;
  }

  private handleSearchInputOpen = async (searchTerm: string) => {
    await this.createSearchCategory(searchTerm);
  };

  private handleSearchInputChange = async (newTerm: string) => {
    if (!newTerm && this.rootStore.recentlyPlayedStore.recentlyPlayedIds.length) {
      await this.createSearchCategory();
      return;
    }
    if (this.rootStore.categoriesStore.currentCategory) {
      this.rootStore.categoriesStore.currentCategory.subCategoryGames = [];
    }
    this.rootStore.categoriesStore.setPageByCategory({ [this.rootStore.categoriesStore.currentId]: 0 });
    this.setRecentlyPlayedShowed(false);
    await this.rootStore.categoriesStore.getNextSearchGames('search-games', this.searchTerm, this.searchGamesLimit);
  };

  public openSearch(searchTerm = '') {
    this._openSearch();
    void this.handleSearchInputOpen(searchTerm);
  }

  public cleanSearch() {
    this.localSaveSearchHistory(this.searchTerm);
    this.setSearchTerm('');
  }

  public closeSearch() {
    this._closeSearch();
    if (this.rootStore.categoriesStore.currentId === 'search') {
      this.rootStore.categoriesStore.selectCategory(HOME_CATEGORY_ID);
    }
    this.localSaveSearchHistory(this.searchTerm);
  }

  private async getInitialSearchGames(searchTerm: string): Promise<GamesResponse> {
    const recentlyPlayedGames = await this.rootStore.recentlyPlayedStore.getRecentlyPlayedGames();

    if (recentlyPlayedGames.length > 0 && !searchTerm) {
      this.setRecentlyPlayedShowed(true);

      return {
        games: recentlyPlayedGames,
        hasMoreGames: false,
      };
    }

    this.setRecentlyPlayedShowed(false);
    return this.searchInitialGames();
  }

  private async searchInitialGames(): Promise<GamesResponse> {
    const pagination = getPagination(1, GAME_PAGINATION_LIMIT, this.searchGamesLimit);

    if (!pagination) {
      return {
        games: [],
        hasMoreGames: false,
      };
    }

    const searchTerm = this.searchTerm.trim();

    if (!searchTerm) {
      return {
        games: [],
        hasMoreGames: false,
      };
    }

    const response = await getRequest<ApiResponse<SubCategoryGamesResponse>>('games/search', {
      limit: pagination.limit,
      page: pagination.page,
      text: searchTerm,
    });

    const { rows: games, hasMoreGames } = get(response, 'data.data', { rows: [], count: 0, hasMoreGames: false });
    return { games, hasMoreGames };
  }

  private async createSearchCategory(searchTerm: string = '') {
    this.rootStore.categoriesStore.setLoading('search-games', true);

    try {
      const { games, hasMoreGames } = await this.getInitialSearchGames(searchTerm);

      this.rootStore.categoriesStore.setCategory('search', {
        createdAt: '',
        name: {
          [SupportedLanguages.EN]: 'Search',
          [SupportedLanguages.DE]: 'Suche',
        },
        subCategoryGames: [],
        masterGameSubCategoryId: SEARCH_CATEGORY_ID,
        imageUrl: '',
        hasMoreGames,
        orderId: -1,
        masterGameCategoryId: -1,
      });

      this.rootStore.categoriesStore.selectCategory('search');

      const { currentCategory } = this.rootStore.categoriesStore;

      if (currentCategory && games.length > 0) {
        this.rootStore.categoriesStore.addGamesToCategory('search', games);
        this.rootStore.categoriesStore.setPageByCategory({ [this.rootStore.categoriesStore.currentId]: 1 });
      } else {
        this.rootStore.categoriesStore.registerGames(games);
      }
    } finally {
      this.rootStore.categoriesStore.setLoading('search-games', false);
    }
  }

  public removeSearchHistoryItem = (index: number) => {
    const newSearchHistory = this.searchHistory.filter((_, i) => i !== index);
    this.setSearchHistory(newSearchHistory);
    localStorage.setItem('games_search_history', JSON.stringify(newSearchHistory));
  };

  public selectSearchHistoryItem = (index: number) => {
    const searchValue = this.searchHistory[index];
    if (!searchValue) {
      return;
    }
    this.setSearchTerm(searchValue);
    this.openSearch(searchValue);
  };

  private localSaveSearchHistory(searchValue: string) {
    if (!searchValue) {
      return;
    }
    const searchHistory = localStorage.getItem('games_search_history');
    const parsedHistory = searchHistory ? (JSON.parse(searchHistory) as string[]) : [];
    const newSearchHistorySet = new Set([searchValue, ...parsedHistory]);
    const newSearchHistoryArray = Array.from(newSearchHistorySet).slice(0, 10);
    this.setSearchHistory(newSearchHistoryArray);
    localStorage.setItem('games_search_history', JSON.stringify(newSearchHistoryArray));
  }

  private syncLocalSearchData() {
    try {
      const searchHistory = localStorage.getItem('games_search_history');
      const parsedHistory = searchHistory ? (JSON.parse(searchHistory) as string[]) : [];
      this.setSearchHistory(parsedHistory);
    } catch {}
  }

  private onPageClose = () => {
    this.localSaveSearchHistory(this.searchTerm);
  };
}
